export const ROLES = ["Copywriter", "Editor", "Creative"];

export const SECOND_MS = 1000;
export const QUARTER_SECOND_MS = SECOND_MS / 4;
export const SHORT_DELAY_MS = 50;
export const FAST_UI_TRANSITION_MS = 160;
export const ROW_TRANSITION_MS = 220;
export const ROW_TRANSITION_RESET_MS = 260;
export const TWO_SECONDS_MS = 2 * SECOND_MS;
export const TWO_POINT_TWO_SECONDS_MS = 2.2 * SECOND_MS;
export const FOUR_SECONDS_MS = 4 * SECOND_MS;
export const FIVE_SECONDS_MS = 5 * SECOND_MS;
export const DEFAULT_FEEDBACK_DURATION_MS = 5200;
export const ONE_MINUTE_MS = 60 * SECOND_MS;
export const FIVE_MINUTES_MS = 5 * ONE_MINUTE_MS;
export const THIRTY_MINUTES_MS = 30 * ONE_MINUTE_MS;
export const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;
export const ONE_DAY_MS = 24 * ONE_HOUR_MS;
export const AUTH_EMAIL_COOLDOWN_SECONDS = 30;

export const TURNSTILE_VERIFICATION_REQUIRED_MESSAGE =
  "Please complete the verification and try again.";

export const VALID_EMAIL_REQUIRED_MESSAGE = "Please enter a valid email.";

export const UNTITLED_PROJECT_LABEL = "Untitled project";
export const UNNAMED_CLIENT_LABEL = "Unnamed client";
export const UNTITLED_CLIENT_LABEL = "Untitled client";
export const UNTITLED_REFERENCE_LABEL = "Untitled reference";
export const UNTITLED_ASSET_LABEL = "Untitled asset";
export const NO_OPTIONS_AVAILABLE_LABEL = "No options available";

export const EDIT_REFERENCE_LABEL = "Edit reference";
export const DOWNLOAD_REFERENCE_LABEL = "Download original PDF";
export const DOWNLOAD_ANNOTATED_REFERENCE_LABEL = "Download annotated PDF";
export const DELETE_REFERENCE_LABEL = "Delete";
export const DELETE_ANNOTATION_LABEL = "Delete annotation";

export const SHOW_PASSWORD_LABEL = "Show password";
export const HIDE_PASSWORD_LABEL = "Hide password";

export const NOT_AUTHENTICATED_ERROR_MESSAGE = "Not authenticated";

export const SIDEBAR_SUBTITLES = [
  "From source to sign-off",
  "From annotation to AFU",
  "From SmPC to submission",
  "One MLR workspace",
  "Annotate. Claim. Approve.",
  "Your MLR pipeline",
  "Reference. Annotate. Approve.",
  "Built for MLR teams",
  "Your team, in sync",
  "Traceable by design",
  "Faster to AFU",
  "MLR at full speed",
  "Claim with confidence",
  "Literature to launch",
  "Brand claims, backed",
];
export const PHARMA_LINES = [
  // --- SHORT (40-55 chars) ---
  "Drug reduced relapse risk across all trial cohorts.",
  "Responder rate favored treatment in biomarker-high.",
  "Guidelines now recommend therapy as a second line.",
  "Safety board reported no unexpected new concerns.",
  "Median time to response shortened vs placebo arm.",
  "Open-label extension confirmed tolerability profile.",
  "Endpoint met with strong efficacy signal in phase III.",
  "Serious adverse events remained rare in all patients.",
  "Postmarketing registry planned for adverse event tracking.",
  "Dose reductions required in less than 10% of cases.",
  "Hospital readmissions declined under early treatment.",
  "Trial confirmed safety profile in older adult groups.",
  "No new safety signals identified in long-term follow-up.",
  "Adherence improved when switched to once-daily dosing.",
  "Patient advocacy groups welcomed tolerability profile.",
  "Immunogenicity remained transient and clinically irrelevant.",
  "Comparator aligned with standard of care at label dose.",
  "Risk mitigation plan includes REMS educational elements.",
  "Observed QTc prolongation was not clinically significant.",
  "Durable responses confirmed across trial extension arms.",

  // --- MEDIUM (56-80 chars) ---
  "Kaplan-Meier analysis showed one-year survival 74% vs 59%.",
  "Consensus panel recommended adoption into national guidelines.",
  "At week 12, remission achieved in 39% vs 17% of placebo patients.",
  "Durable remission maintained through twelve-month follow-up.",
  "Primary endpoint achieved with a robust statistical margin.",
  "Pediatric subgroup outcomes consistent with adult findings.",
  "Quality-of-life scores improved across functional domains.",
  "Trial design was multicenter, randomized, and placebo-controlled.",
  "Hospitalization frequency dropped markedly in active group.",
  "Blinding integrity confirmed after database lock procedures.",
  "Stability data supported 24-month refrigerated shelf life.",
  "Serious adverse events occurred in 4% vs 6% placebo group.",
  "Hazard ratio for relapse observed at 0.62 favoring active.",
  "Healthcare utilization decreased in intervention patients.",
  "FDA reviewers highlighted consistency with earlier approvals.",
  "Responder definitions were prespecified and externally audited.",
  "Drug metabolism minimal via CYP3A4 pathway interactions.",
  "Comparator arm demonstrated expected standard-of-care effects.",
  "Regulatory reviewers emphasized importance of subgroup data.",
  "Observed survival benefit consistent across geographies.",
  "Number needed to treat estimated at six patients overall.",
  "Observed death rate was 2.1% vs 3.2% in control arm.",
  "Extension study confirmed durable efficacy in long-term users.",
  "Observed hazard ratio confidence intervals excluded unity.",
  "Randomization maintained allocation concealment effectively.",
  "Patient diaries recorded significant functional improvements.",
  "No dose interruptions required in most treated participants.",
  "Week 24 analysis showed steroid use fell from 48% to 29%.",
  "Trial powered at 90% to detect a clinically relevant delta.",
  "Immunogenicity events were infrequent and transient in nature.",

  // --- LONG (81-105 chars) ---
  "Median treatment duration was 13.8 months compared with 7.2 months, doubling exposure to active drug.",
  "Median overall survival extended to 22.4 months vs 18.1 months in control, with clear separation.",
  "Adherence rates improved to 87% on daily dosing compared with 71% baseline adherence.",
  "Kaplan-Meier curves separated early and remained apart throughout long-term observation.",
  "Incidence of grade >=3 neutropenia was 6% vs 2%, consistent with mechanism-related expectations.",
  "Cumulative safety profile remained consistent with earlier phases and prior trial observations.",
  "Open-label extension confirmed no emergent safety signals over an additional two-year follow-up.",
  "Relative risk reduction in flare events was calculated at 42% in active vs placebo arms.",
  "Mean change in FEV1 was +210 mL vs +35 mL placebo, reflecting a meaningful pulmonary benefit.",
  "Time to symptom resolution shortened by an average of 5.3 days compared with placebo controls.",
  "Durable remission observed in 68% of treated patients compared with 22% on placebo at 12 months.",
  "C-reactive protein levels decreased by 47% in treated participants vs 12% in placebo.",
  "Observed discontinuation due to adverse events occurred in 5% vs 9% of placebo participants.",
  "FDA briefing documents emphasized unmet medical need and favorable benefit-risk assessment.",
  "EMA requested additional data on dose-response relationship prior to broader label approval.",
  "Observed hazard ratio confidence interval excluded unity, confirming robustness of outcomes.",
  "Consensus guidelines updated to reflect survival benefit demonstrated in pivotal trials.",
  "Blinded adjudication committee confirmed accuracy of all primary outcome classifications.",
  "Systematic review reinforced reproducibility, showing consistent efficacy across designs.",
  "Hospitalization frequency reduced from 32 to 18 events per 100 patient-years in trial.",
  "Annualized relapse rate was 0.19 in treated patients compared with 0.43 for placebo.",
  "Trial registered with ClinicalTrials.gov, full protocol publicly accessible for review.",
  "Extension study confirmed 76% of patients remained on therapy after 28 months.",
  "Patient preference surveys indicated strong favorability for oral over injectable routes.",
  "Consensus panel recommended inclusion in national reimbursement pathways based on results.",
  "Placebo-adjusted change in primary outcome exceeded the clinically relevant threshold.",
  "Regulators noted limitations in pediatric subgroup sample size affecting power estimates.",
  "Trial limitations included underrepresentation of certain ethnic subgroups in recruitment.",
  "Responder analysis was consistent across prespecified and sensitivity analysis methods.",
  "Observed LDL cholesterol reduction averaged 28 mg/dL vs 4 mg/dL in placebo arm.",

  // --- EXTRA LONG (106-125 chars) ---
  "Median overall survival extended to 22.4 months in the treatment group compared with 18.1 months for those on placebo.",
  "Systematic review highlighted reproducibility, confirming consistent outcomes across different geographies and trial designs.",
  "Kaplan-Meier survival analysis showed early curve separation that remained sustained through extended follow-up periods.",
  "Adherence to therapy increased to 87% in the once-daily arm compared with 71% baseline, supporting simplified dosing regimens.",
  "Hospital readmissions declined significantly, reflecting improved disease control and reduced healthcare utilization overall.",
  "Observed safety profile was consistent across all phases of development, with no unexpected toxicity emerging long-term.",
  "FDA advisory committee voted unanimously in favor of approval, citing substantial evidence of efficacy and safety balance.",
  "Pediatric cohort data aligned closely with adult efficacy results, supporting consideration of label expansion to younger ages.",
  "Health-economic modeling projected long-term payer savings, driven by reduced exacerbations and fewer hospital admissions.",
  "Relative risk reduction in flare events was estimated at 42%, consistent across subgroups and sensitivity analyses.",
  "Trial demonstrated superiority to placebo, with the primary endpoint met and secondary endpoints achieved concurrently.",
  "Durable responses were confirmed, with 68% of patients maintaining remission at one year compared with 22% on placebo.",
  "Consensus guidelines incorporated findings into clinical pathways, emphasizing benefit-risk profile in severe disease.",
  "EMA assessment highlighted the absence of boxed warning requirement, based on aggregate safety data across all studies.",
  "Observed QTc prolongation >=30 ms occurred in 1.8% vs 1.6% placebo, deemed not clinically meaningful by investigators.",
  "Regulatory reviewers emphasized importance of ongoing postmarketing surveillance to confirm safety in broader populations.",
  "Cumulative incidence of relapse was lower in intervention arms, reinforcing trial conclusions of durable benefit.",
  "Blinded endpoint review committee upheld trial results, ensuring unbiased adjudication of clinical outcomes throughout.",
  "Trial met statistical threshold for superiority, confirming both coprimary endpoints without hierarchical penalty breach.",
  "Postmarketing commitments include rare adverse event monitoring, with registries established across multiple geographies.",
];

export const HELP_FAQS = [
  // Getting Started
  {
    category: "Getting Started",
    q: "Where do I start after signing in?",
    a: "Head over to your Reference Library to upload PDFs and start annotating!",
  },
  {
    category: "Getting Started",
    q: "Where do I find my preferences and settings?",
    a: "Go to Settings from your profile menu to update your password, dashboard layout, annotation theme color, and team or billing details.",
  },

  // Workspace
  {
    category: "Workspace",
    q: "How is work organized in Refanno?",
    a: "Work is organized by Client → Brand → Project. Each client groups related brands and projects, and each project holds its reference PDFs, annotations, citations, and assets.",
  },
  {
    category: "Workspace",
    q: "How do I create a new client, brand, or project?",
    a: "You can manage these in your Portfolio. You can also create any of them on the fly during a reference or asset upload.",
  },
  {
    category: "Workspace",
    q: "Should I archive or delete old items?",
    a: "Use Archive when you want something out of active views but still recoverable. Permanent deletion is a separate workflow and varies by item type.",
  },
  {
    category: "Workspace",
    q: "Can I filter by client or project?",
    a: "Yes, most views support filtering by client, brand, or project.",
  },

  // Reference PDFs
  {
    category: "Reference PDFs",
    q: "What file types can I upload as references?",
    a: "References support PDF files only.",
  },
  {
    category: "Reference PDFs",
    q: "Is there a file size limit?",
    a: "Yes - 20MB per file. If your PDF is larger, try compressing it first.",
  },
  {
    category: "Reference PDFs",
    q: "How do I upload a reference?",
    a: "In Reference Library → Reference Files, click Upload. Select a client and project, then drag and drop or browse for a PDF. You can also set a reference type (Literature, SmPC, Clinical Study, etc.) during upload.",
  },
  {
    category: "Reference PDFs",
    q: "Can I edit a reference after uploading?",
    a: "Yes. Use the edit action on any reference row to update the title, notes, reference type, or project assignment.",
  },
  {
    category: "Reference PDFs",
    q: "How do I download a reference?",
    a: "Use the download action on any reference row in Reference Library. The file downloads using the reference title as the filename.",
  },

  // PDF Viewer
  {
    category: "PDF Viewer",
    q: "How do I open a reference for annotation?",
    a: "In Reference Library → Reference Files, click the Open action on any row to launch it in the viewer.",
  },
  {
    category: "PDF Viewer",
    q: "What annotation tools are available?",
    a: "The viewer supports text-selection annotations (highlight + note), freehand ink, free text, and image placement.",
  },
  {
    category: "PDF Viewer",
    q: "How do I create an annotation from selected text?",
    a: "Select text in the viewer. A floating menu appears - choose Create Annotation. The annotation is saved to the Annotations drawer with the selected quote.",
  },
  {
    category: "PDF Viewer",
    q: "Do I need to manually save my annotations?",
    a: "Yes. After making changes, click Save in the annotation toolbar. If Save is grayed out, there are no unsaved changes.",
  },
  {
    category: "PDF Viewer",
    q: "Can I search within a document?",
    a: "Yes. Open the Search drawer in the viewer toolbar to find text in the current PDF.",
  },
  {
    category: "PDF Viewer",
    q: "Can I adjust page layout or capture a screenshot?",
    a: "Yes. The page settings menu lets you rotate pages and switch scroll/layout modes. Use the viewer menu → Capture screenshot to save a PNG of the current page including annotations.",
  },

  // Annotations
  {
    category: "Annotations",
    q: "Where can I see all annotations across documents?",
    a: "Go to Reference Library → Annotations to view, search, filter, and manage all annotations across your references.",
  },
  {
    category: "Annotations",
    q: "Can I edit or delete an annotation?",
    a: "Yes. Use the row actions in Reference Library → Annotations, or manage annotations directly from the Annotations drawer inside the viewer.",
  },
  {
    category: "Annotations",
    q: 'Why does an annotation say "No text captured"?',
    a: "Freehand ink and image placement annotations don't include a text quote. Only text-selection annotations capture the highlighted passage automatically.",
  },
  {
    category: "Annotations",
    q: "How are annotations tied to a reference?",
    a: "Annotations are linked by reference ID at creation. Reference Library shows the parent reference title alongside each annotation.",
  },

  // Brand Claims
  {
    category: "Brand Claims",
    q: "What are brand claims?",
    a: "Brand claims are reusable, centrally managed statements you place onto assets. Each claim can be backed by supporting annotations and linked to citations for traceability.",
  },
  {
    category: "Brand Claims",
    q: "How do I create or edit a brand claim?",
    a: "Go to Brand Claims and click New Claim. Enter the claim text, assign a category if needed, and save as a draft. Edit anytime using the row action or by opening the claim.",
  },
  {
    category: "Brand Claims",
    q: "What do the claim statuses mean?",
    a: "Draft - editable, not yet submitted. Pending approval - submitted for admin review, locked for editing. AFU (Approved for Use) - reviewed and cleared for use in assets. Rejected - sent back with a reason; revise and resubmit.",
  },
  {
    category: "Brand Claims",
    q: "How does the claim approval workflow work?",
    a: "A team member submits a draft claim for approval. An admin reviews it from Brand Claims → Approvals and either marks it AFU or rejects it with a reason. Only AFU claims can be placed on assets.",
  },
  {
    category: "Brand Claims",
    q: "What does pinning a claim do?",
    a: "Pinned claims are marked as favorites and appear at the top of claim pickers in the Asset Editor for faster access.",
  },
  {
    category: "Brand Claims",
    q: "How are claims connected to annotations?",
    a: "Annotations can be linked to a claim to document supporting evidence. The Brand Claims table shows an annotation count for each claim so you can track substantiation coverage.",
  },

  // Citations
  {
    category: "Citations (AMA)",
    q: "What citation format does Refanno use?",
    a: "Citations are formatted in AMA style (American Medical Association).",
  },
  {
    category: "Citations (AMA)",
    q: "How do I create a citation?",
    a: "Go to Reference Library → Citations and click New Citation. Select a reference and the system drafts a citation from the PDF metadata. Edit any fields before saving.",
  },
  {
    category: "Citations (AMA)",
    q: "Can I paste an existing AMA citation?",
    a: "Yes. On the compose step, paste a formatted AMA citation and the fields will auto-populate. Edit before saving.",
  },
  {
    category: "Citations (AMA)",
    q: "Can I edit or delete citations later?",
    a: "Yes. Use the row actions in Reference Library → Citations to edit or delete any citation.",
  },

  // Assets & Asset Editor
  {
    category: "Assets & Asset Editor",
    q: "What are assets?",
    a: "Assets are PDF or image files - usually promotional deliverables or drafts - that you upload, edit with overlays in the Asset Editor, and submit for approval.",
  },
  {
    category: "Assets & Asset Editor",
    q: "What file types are supported for assets?",
    a: "Assets support PDF, PNG, JPG/JPEG, WEBP, and GIF files up to 20MB.",
  },
  {
    category: "Assets & Asset Editor",
    q: "How do I place a brand claim on an asset?",
    a: "Open the asset in the Asset Editor and add a claim overlay. Select the claim from your AFU claim list, position it on the canvas, and style as needed.",
  },
  {
    category: "Assets & Asset Editor",
    q: "What is a ref block?",
    a: "A ref block is an overlay that auto-generates the formatted citation for a claim placed on the asset. When the underlying claim changes, the ref block stays in sync automatically.",
  },
  {
    category: "Assets & Asset Editor",
    q: "How do I export a finished asset?",
    a: "In the Asset Editor, use Export to download a PDF of the asset with all overlays included.",
  },

  // Inbox & Messaging
  {
    category: "Inbox & Messaging",
    q: "How does the Inbox work?",
    a: "Inbox is your team message center. You receive notifications here for mentions, task updates, thread replies, and system activity. Open a thread to respond inline.",
  },
  {
    category: "Inbox & Messaging",
    q: "How do I mention a teammate?",
    a: "In a message thread, type @ followed by their name to mention them. They'll receive a notification in their Inbox.",
  },
  {
    category: "Inbox & Messaging",
    q: "How do I manage notifications?",
    a: "Open Inbox and mark individual messages as read, or use mark-all-read to clear your unread count at once.",
  },

  // Change History & Archive
  {
    category: "Change History & Archive",
    q: "What is Change History?",
    a: "Change History is a full audit log of every action taken across your team - who changed what, and when. Access it from the sidebar to review recent activity or track specific edits.",
  },
  {
    category: "Change History & Archive",
    q: "How do I archive something?",
    a: "Use the archive action on any client, project, reference, annotation, citation, or asset. Archived items are hidden from active views but preserved and accessible from the Archive page.",
  },
  {
    category: "Change History & Archive",
    q: "What is the difference between archiving and deleting?",
    a: "Archiving is reversible - you can restore items from the Archive page at any time. Deletion via Archive is permanent and cannot be undone.",
  },

  // Teams & Invites
  {
    category: "Teams & Invites",
    q: "What roles are available?",
    a: "There are three roles: Owner (full org control - teams, billing, settings), Admin (manages team membership and brand claim approvals), and Member (creates and collaborates on references, annotations, assets, and claims).",
  },
  {
    category: "Teams & Invites",
    q: "How do I invite a teammate?",
    a: "Go to Settings → My Team → Invites. Owners and admins can generate an invite link to share directly with the new team member.",
  },
  {
    category: "Teams & Invites",
    q: "Where do I see and manage pending invites?",
    a: "Go to Settings → My Team → Invites to view active invite links and revoke any that are no longer needed.",
  },
  {
    category: "Teams & Invites",
    q: "Can someone belong to multiple teams?",
    a: "Not simultaneously. Each user belongs to one team at a time. Org owners can reassign members between teams from Settings → My Team.",
  },

  // Account & Settings
  {
    category: "Account & Settings",
    q: "How do I change my password?",
    a: "Go to Settings and find the password section. You'll confirm your current password before setting a new one.",
  },
  {
    category: "Account & Settings",
    q: "What should I do if I can't log in?",
    a: "Use Reset Password from the login screen. After resetting, sign in with your updated credentials. If problems persist, use the support form on this page.",
  },
  {
    category: "Account & Settings",
    q: "Why was I logged out unexpectedly?",
    a: "Sessions expire after inactivity or if your credentials are invalidated. Refresh the page and sign back in - your data is preserved.",
  },
  {
    category: "Account & Settings",
    q: "How do I customize my Dashboard?",
    a: "On the Dashboard, toggle Edit Mode to add, remove, or rearrange widgets. Layout changes are auto-saved when you exit edit mode.",
  },
  {
    category: "Account & Settings",
    q: "How do I change the annotation editor accent color?",
    a: "Go to Settings → Preferences and update the theme color. This changes the accent color in the PDF viewer and annotation toolbar.",
  },

  // Billing
  {
    category: "Billing",
    q: "Where do I see my current plan?",
    a: "Go to Settings → Billing. Your current plan, seat count, and renewal details are shown there.",
  },
  {
    category: "Billing",
    q: "How do I manage my subscription?",
    a: "From Settings → Billing, open the secure billing portal to change plans, update payment details, manage seats, and download invoices.",
  },
  {
    category: "Billing",
    q: "How do I contact support about a billing issue?",
    a: "Use the support form on this Help page. Include your account email and a description of the issue - our team will follow up directly.",
  },

  // Troubleshooting
  {
    category: "Troubleshooting",
    q: "My PDF won't upload - what should I check?",
    a: "Confirm the file is a PDF and under 20MB. If it still fails, try refreshing the page and uploading again. If the problem persists, use the support form.",
  },
  {
    category: "Troubleshooting",
    q: "The PDF viewer is blank or the document won't load",
    a: "Try refreshing the viewer. If it persists, go to Reference Library, re-download the file to confirm it's valid, then re-upload if needed.",
  },
  {
    category: "Troubleshooting",
    q: "My annotations aren't showing up",
    a: "Make sure you clicked Save in the annotation toolbar before leaving the viewer. If you did save, refresh the viewer and open the Annotations drawer to confirm they loaded.",
  },
  {
    category: "Troubleshooting",
    q: "The support form asks me to complete a verification step",
    a: "Some actions require Cloudflare Turnstile verification to prevent spam. Complete the check and submit again.",
  },
];
